import React, { useRef, useState } from 'react'
import { TextField } from '@material-ui/core'
import { Controller } from 'react-hook-form'

import { extractFullPostcode } from '../../lib/postcode'
import { postcodeCheckResult, postcodeNote, postcodeOutward } from '../../lib/postcode-check'
import { AnalyticsService } from '../../services/analytics'

/**
 * The car's location: ONE box, "Car location" (lib/car-location.js).
 *
 * History, because the shape of this file is the whole point. It was a
 * Postcoder autocomplete until 2026-09-17, when the picker was found to be
 * the single biggest drop-off in the booking form (ANALYTICS.md §9). It then
 * became one free-text box whose contents were sent to Google's geocoder, so
 * the order could carry coordinates. Google answers almost anything with
 * SOMETHING - junk came back as "United Kingdom", half a postcode as
 * "London N5" - and each of those answers carried a location, so the rest of
 * the component existed to second-guess it. Two production bugs came out of
 * that design in a single week.
 *
 * On 2026-10-04 the owner's call was to stop guessing by not needing the
 * answer: "we dont need maps, locations, geocoding, its way overkil for the
 * front end". The operator opens the address in their own maps app from a
 * link the server builds out of it (Operator::OrderSerializer#maps_url). From
 * 5 Oct it was two boxes, a postcode and the house number or name; from
 * 19 Oct one Car location box with Royal Mail PAF suggestions while the
 * address lookup is on, the two boxes behind "Enter it manually"; and from
 * 25 Oct 2026 the one box everywhere (Robin: "you dont need house number any
 * more its in the postcode; so postcode should just be 'car location'").
 */

// Does postcodes.io know this postcode? 200 yes, 404 no, null for no answer
// (a timeout, an outage, no signal). Free, no key, open to the browser.
const POSTCODES_IO = 'https://api.postcodes.io/postcodes/'
const askPostcodesIo = async postcode => {
  if (typeof fetch === 'undefined') return null
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), 3000) : null
  try {
    const res = await fetch(`${POSTCODES_IO}${encodeURIComponent(postcode.replace(/\s+/g, ''))}`, controller ? { signal: controller.signal } : {})
    return res.status === 200 || res.status === 404 ? res.status : null
  } catch (e) {
    return null
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * The Car location box. With the address lookup on, the suggestions under
 * it come from the lazy address-pick script, which fills the empty list
 * right below the box (in the page, so it never covers the next field) and
 * hands a pick back as an event StepBook listens to. onEdit runs when the
 * customer types, so a picked address that is then changed stops counting
 * as picked.
 *
 * `free` (the lookup off, or "Enter it manually"): the same box as plain
 * free text, "12 Elm Road, SW6 1RE", with no list (data-free tells the
 * script to leave it alone). On blur the postcode found in the text gets the
 * checks the postcode box used to make: postcodes.io (postcode_checked, the
 * outward code only) and the soft note for a postcode outside the usual area
 * or unknown there. Neither ever refuses (W8, 5 Oct 2026; Robin, 9 Oct
 * 2026); what is missing is said at the pay press (lib/car-location.js
 * locationProblem).
 */
export const CarLocation = React.memo(({ form, name, note, free = false, onFocus, onEdit, onManual, linkClassName }) => {
  // The last postcode asked about and what postcodes.io said.
  const [lookup, setLookup] = useState({ postcode: null, status: null })
  const reported = useRef(null)
  const check = async text => {
    const postcode = extractFullPostcode(text)
    if (!postcode) return
    let status = lookup.postcode === postcode ? lookup.status : null
    if (lookup.postcode !== postcode) {
      status = await askPostcodesIo(postcode)
      setLookup({ postcode, status })
    }
    if (reported.current !== postcode) {
      reported.current = postcode
      AnalyticsService.trackPostcodeChecked(postcodeCheckResult(postcode, status), postcodeOutward(postcode))
    }
  }
  return (
    <>
      <Controller
        name={name}
        control={form.control}
        render={({ field: { ref, onChange, onBlur, ...field }, fieldState: { error } }) => {
          const typed = free ? extractFullPostcode(field.value) : null
          const freeNote = typed ? postcodeNote(typed, lookup.postcode === typed ? lookup.status : null) : null
          return (
            <TextField
              {...field}
              onChange={e => { onChange(e); onEdit && onEdit() }}
              onBlur={() => { onBlur(); if (free) check(field.value) }}
              // The ref on the input, not on MUI's root div, so setFocus works.
              inputRef={ref}
              id={`book-${name}`}
              fullWidth
              placeholder={free ? 'Street address and postcode' : 'Start typing a postcode or street'}
              error={!!error}
              helperText={error?.message || (free ? freeNote : note) || null}
              InputProps={{ autoComplete: free ? 'street-address' : 'off', disableUnderline: true, onFocus }}
              InputLabelProps={{ shrink: true }}
              // Recorded as typed in PostHog replays (data-ph-unmask, read by
              // the layout's maskInputFn; docs/DECISIONS.md, 17 Oct 2026).
              inputProps={free
                ? { spellCheck: 'false', 'data-free': 'true', 'data-ph-unmask': 'true' }
                : { role: 'combobox', 'aria-autocomplete': 'list', 'aria-controls': `book-${name}-list`, 'aria-expanded': 'false', spellCheck: 'false', 'data-ph-unmask': 'true' }}
            />
          )
        }}
      />
      {!free && <div id={`book-${name}-list`} role="listbox" aria-label="Addresses" hidden />}
      {!free && onManual && <button type="button" className={linkClassName} onClick={onManual} data-testid="car-location-manual">Can't find it? Enter it manually</button>}
    </>
  )
})
